import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const PyodideContext = createContext(null);

const PYODIDE_CDN = 'https://cdn.jsdelivr.net/pyodide/v0.24.1/full/';

export function PyodideProvider({ children }) {
  const [pyodide, setPyodide] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState('Initializing...');

  useEffect(() => {
    async function loadPyodide() {
      try {
        setLoadingStatus('Loading Python runtime...');

        // Load Pyodide script
        if (!window.loadPyodide) {
          const script = document.createElement('script');
          script.src = `${PYODIDE_CDN}pyodide.js`;
          script.async = true;

          await new Promise((resolve, reject) => {
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

        setLoadingStatus('Initializing Python...');
        const pyodideInstance = await window.loadPyodide({
          indexURL: PYODIDE_CDN,
        });

        setLoadingStatus('Loading M interpreter...');

        // Load the M interpreter code (with cache-busting)
        const interpreterCode = await fetch(`/python/engine/interpreter_bundle.py?v=${Date.now()}`)
          .then(r => r.text());

        await pyodideInstance.runPythonAsync(interpreterCode);

        // Create helper function for running M code
        await pyodideInstance.runPythonAsync(`
def run_m_code(source):
    """Run M code and return result as JSON-serializable dict."""
    import json
    try:
        result = run(source)
        return {
            'success': True,
            'result': format_value(result),
            'structured': format_structured(result),
            'type': result.type_name()
        }
    except MError as e:
        return {
            'success': False,
            'error': str(e)
        }
    except Exception as e:
        return {
            'success': False,
            'error': f'Unexpected error: {str(e)}'
        }

def format_value(value):
    """Format M value for display (string comparison)."""
    if isinstance(value, MNull):
        return 'null'
    elif isinstance(value, MNumber):
        v = value.to_python()
        if isinstance(v, float) and v.is_integer():
            return str(int(v))
        return str(v)
    elif isinstance(value, MText):
        return f'"{value.to_python()}"'
    elif isinstance(value, MLogical):
        return 'true' if value.to_python() else 'false'
    elif isinstance(value, MList):
        items = [format_value(item) for item in value.items]
        return '{' + ', '.join(items) + '}'
    elif isinstance(value, MRecord):
        fields = [f'{k} = {format_value(v)}' for k, v in value.fields.items()]
        return '[' + ', '.join(fields) + ']'
    elif isinstance(value, MTable):
        rows = []
        for row in value.rows:
            fields = [f'{col} = {format_value(row[col])}' for col in value.columns]
            rows.append('[' + ', '.join(fields) + ']')
        return '{' + ', '.join(rows) + '}'
    elif isinstance(value, MFunction):
        return '<function>'
    elif isinstance(value, MDate):
        d = value.to_python()
        if isinstance(d, str):
            return d
        if isinstance(d, dict):
            return f'{d.get("year", 0)}-{d.get("month", 0):02d}-{d.get("day", 0):02d}'
        return f'{d.year}-{d.month:02d}-{d.day:02d}'
    elif isinstance(value, MTime):
        t = value.to_python()
        if isinstance(t, str):
            return t
        if isinstance(t, dict):
            return f'{t.get("hour", 0):02d}:{t.get("minute", 0):02d}:{t.get("second", 0):02d}'
        return f'{t.hour:02d}:{t.minute:02d}:{t.second:02d}'
    elif isinstance(value, MDateTime):
        dt = value.to_python()
        if isinstance(dt, str):
            return dt
        if isinstance(dt, dict):
            return f'{dt.get("year", 0)}-{dt.get("month", 0):02d}-{dt.get("day", 0):02d}T{dt.get("hour", 0):02d}:{dt.get("minute", 0):02d}:{dt.get("second", 0):02d}'
        return f'{dt.year}-{dt.month:02d}-{dt.day:02d}T{dt.hour:02d}:{dt.minute:02d}:{dt.second:02d}'
    elif isinstance(value, MDuration):
        dur = value.to_python()
        if isinstance(dur, str):
            return dur
        if isinstance(dur, dict):
            days = dur.get("days", 0)
            hours = dur.get("hours", 0)
            minutes = dur.get("minutes", 0)
            seconds = dur.get("seconds", 0)
        else:
            total_seconds = int(dur.total_seconds())
            days = total_seconds // 86400
            remaining = total_seconds % 86400
            hours = remaining // 3600
            remaining = remaining % 3600
            minutes = remaining // 60
            seconds = remaining % 60
        return f'{days}.{hours:02d}:{minutes:02d}:{seconds:02d}'
    elif isinstance(value, MDateTimeZone):
        dtz = value.to_python()
        if isinstance(dtz, str):
            return dtz
        if isinstance(dtz, dict):
            offset_hours = dtz.get("offsetHours", 0)
            offset_minutes = dtz.get("offsetMinutes", 0)
            sign = '+' if offset_hours >= 0 else '-'
            return f'{dtz.get("year", 0)}-{dtz.get("month", 0):02d}-{dtz.get("day", 0):02d}T{dtz.get("hour", 0):02d}:{dtz.get("minute", 0):02d}:{dtz.get("second", 0):02d}{sign}{abs(offset_hours):02d}:{abs(offset_minutes):02d}'
        offset = dtz.utcoffset()
        offset_hours = int(offset.total_seconds() // 3600) if offset else 0
        offset_minutes = int((offset.total_seconds() % 3600) // 60) if offset else 0
        sign = '+' if offset_hours >= 0 else '-'
        return f'{dtz.year}-{dtz.month:02d}-{dtz.day:02d}T{dtz.hour:02d}:{dtz.minute:02d}:{dtz.second:02d}{sign}{abs(offset_hours):02d}:{abs(offset_minutes):02d}'
    else:
        return str(value.to_python())

def format_structured(value):
    """Format M value as structured data for rich rendering."""
    if isinstance(value, MNull):
        return {'kind': 'scalar', 'type': 'null', 'value': 'null'}
    elif isinstance(value, MNumber):
        return {'kind': 'scalar', 'type': 'number', 'value': format_value(value)}
    elif isinstance(value, MText):
        return {'kind': 'scalar', 'type': 'text', 'value': value.to_python()}
    elif isinstance(value, MLogical):
        return {'kind': 'scalar', 'type': 'logical', 'value': 'true' if value.to_python() else 'false'}
    elif isinstance(value, MList):
        return {
            'kind': 'list',
            'items': [format_structured(item) for item in value.items]
        }
    elif isinstance(value, MRecord):
        return {
            'kind': 'record',
            'fields': [{'name': k, 'value': format_structured(v)} for k, v in value.fields.items()]
        }
    elif isinstance(value, MTable):
        rows_data = []
        for row in value.rows:
            row_items = []
            for col in value.columns:
                row_items.append(format_structured(row[col]))
            rows_data.append(row_items)
        col_types = {}
        if hasattr(value, '_column_types') and value._column_types:
            col_types = {k: v for k, v in value._column_types.items()}
        return {
            'kind': 'table',
            'columns': list(value.columns),
            'columnTypes': col_types,
            'rows': rows_data
        }
    elif isinstance(value, MFunction):
        return {'kind': 'scalar', 'type': 'function', 'value': '<function>'}
    elif isinstance(value, MDate):
        return {'kind': 'scalar', 'type': 'date', 'value': format_value(value)}
    elif isinstance(value, MTime):
        return {'kind': 'scalar', 'type': 'time', 'value': format_value(value)}
    elif isinstance(value, MDateTime):
        return {'kind': 'scalar', 'type': 'datetime', 'value': format_value(value)}
    elif isinstance(value, MDuration):
        return {'kind': 'scalar', 'type': 'duration', 'value': format_value(value)}
    elif isinstance(value, MDateTimeZone):
        return {'kind': 'scalar', 'type': 'datetimezone', 'value': format_value(value)}
    else:
        return {'kind': 'scalar', 'type': 'unknown', 'value': str(value.to_python())}

def run_m_code_with_trace(source):
    """Run M code with execution tracing and return result + trace as JSON-serializable dict."""
    import json
    try:
        result, trace = run_with_trace(source)
        return {
            'success': True,
            'result': format_value(result),
            'structured': format_structured(result),
            'type': result.type_name(),
            'trace': trace
        }
    except MError as e:
        return {
            'success': False,
            'error': str(e),
            'trace': None
        }
    except Exception as e:
        return {
            'success': False,
            'error': f'Unexpected error: {str(e)}',
            'trace': None
        }
`);

        setPyodide(pyodideInstance);
        setIsReady(true);
        setLoadingStatus('Ready!');
      } catch (err) {
        console.error('Failed to load Pyodide:', err);
        setError(err.message);
        setLoadingStatus('Failed to load');
      } finally {
        setIsLoading(false);
      }
    }

    loadPyodide();
  }, []);

  const runMCode = useCallback(async (code) => {
    if (!pyodide || !isReady) {
      return { success: false, error: 'Python runtime not ready' };
    }

    try {
      const result = await pyodide.runPythonAsync(`
import json
json.dumps(run_m_code(${JSON.stringify(code)}))
`);
      return JSON.parse(result);
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [pyodide, isReady]);

  const runMCodeWithTrace = useCallback(async (code) => {
    if (!pyodide || !isReady) {
      return { success: false, error: 'Python runtime not ready', trace: null };
    }

    try {
      const result = await pyodide.runPythonAsync(`
import json
json.dumps(run_m_code_with_trace(${JSON.stringify(code)}))
`);
      return JSON.parse(result);
    } catch (err) {
      return { success: false, error: err.message, trace: null };
    }
  }, [pyodide, isReady]);

  const value = {
    isLoading,
    isReady,
    error,
    loadingStatus,
    runMCode,
    runMCodeWithTrace,
  };

  return (
    <PyodideContext.Provider value={value}>
      {children}
    </PyodideContext.Provider>
  );
}

export function usePyodide() {
  const context = useContext(PyodideContext);
  if (!context) {
    throw new Error('usePyodide must be used within a PyodideProvider');
  }
  return context;
}
